import { IconButton, Tag } from '@ads/components-react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { BannerListItemViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

import { formatBannerDate } from '../../../utils/formatBannerDate';

export interface BannerTableProps {
  readonly data: readonly BannerListItemViewModel[];
  readonly isLoading: boolean;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalCount: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly emptyStateAction?: ReactNode;
  readonly onEdit: (banner: BannerListItemViewModel) => void;
  readonly onDelete: (banner: BannerListItemViewModel) => void;
}

/**
 * Tabela de Banners. Colunas Título, Data inicial, Data final, Geral e Ativo
 * (as duas últimas como `Tag`, para o status não depender só de cor/ícone) e
 * ações inline por linha. Delega loading/empty/paginação (server-side) ao
 * `DataTable`.
 *
 * As ações de editar e excluir são inline: com apenas duas, o menu "..." não se
 * justifica — e é assim que a tela legada de referência as apresenta.
 */
export function BannerTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  emptyStateAction,
  onEdit,
  onDelete,
}: BannerTableProps) {
  const { t } = useTranslation('banner');

  const columns = useMemo<ColumnDef<BannerListItemViewModel, unknown>[]>(
    () => [
      {
        accessorKey: 'title',
        header: t('banner.table.columns.title'),
        cell: info => (
          <span className="ts-paragraph-xxs !font-medium text-ctx-content-title">
            {(info.getValue() as string) ?? ''}
          </span>
        ),
      },
      {
        id: 'startDate',
        accessorKey: 'startDate',
        header: t('banner.table.columns.startDate'),
        cell: ({ row }) => (
          <span className="ts-paragraph-xxs text-ctx-content-base">
            {formatBannerDate(row.original.startDate)}
          </span>
        ),
      },
      {
        id: 'endDate',
        accessorKey: 'endDate',
        header: t('banner.table.columns.endDate'),
        cell: ({ row }) => (
          <span className="ts-paragraph-xxs text-ctx-content-base">
            {formatBannerDate(row.original.endDate)}
          </span>
        ),
      },
      {
        id: 'general',
        accessorKey: 'general',
        header: t('banner.table.columns.general'),
        cell: ({ row }) => {
          const general = row.original.general ?? false;
          return (
            <Tag
              label={t(general ? 'banner.table.valueYes' : 'banner.table.valueNo')}
              size="md"
              status={general ? 'positive' : 'default'}
              custom={{ text: 'text-nowrap' }}
            />
          );
        },
      },
      {
        id: 'active',
        accessorKey: 'active',
        header: t('banner.table.columns.active'),
        cell: ({ row }) => {
          const active = row.original.active ?? false;
          return (
            <Tag
              label={t(active ? 'banner.table.statusActive' : 'banner.table.statusInactive')}
              size="md"
              status={active ? 'positive' : 'default'}
              custom={{ text: 'text-nowrap' }}
            />
          );
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">{t('banner.table.columns.actions')}</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-quark">
            <IconButton
              icon={PencilSquareIcon}
              ariaLabel={t('banner.actions.edit')}
              hierarchy="tertiary"
              size="md"
              onClick={() => onEdit(row.original)}
            />
            <IconButton
              icon={TrashIcon}
              ariaLabel={t('banner.actions.delete')}
              hierarchy="tertiary"
              className="!text-base-danger-600"
              size="md"
              onClick={() => onDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [t, onEdit, onDelete],
  );

  return (
    <DataTable
      data={data as BannerListItemViewModel[]}
      columns={columns}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      onPageChange={onPageChange}
      emptyStateMessage={t('banner.table.empty')}
      emptyStateDescription={t('banner.table.emptyDescription')}
      {...(emptyStateAction ? { emptyStateAction } : {})}
    />
  );
}
